import { useTranslation } from "react-i18next";
import type { AgentRole } from "../../services/naraDoor";

interface AgentConsentProps {
  name: string;
  role: AgentRole;
  accepted: boolean;
  pending: boolean;
  error: string | null;
  onAcceptedChange: (accepted: boolean) => void;
  onConfirm: () => void;
  onBack: () => void;
  /** i18n keys of the confirm button; the add flow's by default. */
  confirmLabel?: string;
  pendingLabel?: string;
}

/** D5: the admin reads what the agent can do and ticks the box before the agent exists. */
export function AgentConsent({ name, role, accepted, pending, error, onAcceptedChange, onConfirm, onBack, confirmLabel = "agent.add.create", pendingLabel = "agent.add.creating" }: AgentConsentProps) {
  const { t } = useTranslation();
  return (
    <div className="mt-4 space-y-4">
      <div className="p-3 bg-muted rounded-lg text-sm space-y-2">
        <p className="font-medium text-foreground">{t("agent.consent.summary", { name: name.trim(), role: t(`workspaceMembers.roles.${role}`) })}</p>
        <p className="text-foreground">{t("agent.consent.intro")}</p>
        <ul className="list-disc pl-5 space-y-1 text-foreground">
          <li>{t("agent.consent.read")}</li>
          <li>{t("agent.consent.edit")}</li>
          <li>{t("agent.consent.key")}</li>
          <li>{t("agent.consent.revoke")}</li>
        </ul>
      </div>

      <label className="flex items-start gap-2">
        <input type="checkbox" checked={accepted} onChange={(e) => onAcceptedChange(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-border" />
        <span className="text-sm text-foreground">{t("agent.consent.checkbox")}</span>
      </label>

      {error && (
        <div role="alert" className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-3">
        <button type="button" onClick={onBack} disabled={pending} className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors disabled:opacity-50">
          {t("agent.add.back")}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={!accepted || pending}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {pending ? t(pendingLabel) : t(confirmLabel)}
        </button>
      </div>
    </div>
  );
}
